import React, { useState, useEffect, useRef } from 'react';
import constant from '../../constant';

const ItemForm = ({
  onSubmit, onCancel, loading: parentLoading, isEditing: parentIsEditing, error: initialError,
  name: initialName, gstRate: initialGstRate, price: initialPrice, isTaxInclusive: initialIsTaxInclusive,
  itemType: initialItemType, hsnCode: initialHsnCode, unit: initialUnit, isEditMode = false
}) => {
  const [name, setName] = useState('');
  const [gstRate, setGstRate] = useState('');
  const [price, setPrice] = useState('');
  const [isTaxInclusive, setIsTaxInclusive] = useState(false);
  const [itemType, setItemType] = useState('');
  const [hsnCode, setHsnCode] = useState('');
  const [unit, setUnit] = useState('');

  const [searchHsnTerm, setSearchHsnTerm] = useState('');
  const [hsnOptions, setHsnOptions] = useState([]);
  const [hsnLoading, setHsnLoading] = useState(false);
  const [hsnError, setHsnError] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const debounceTimeout = useRef(null);
  const hsnInputRef = useRef(null);
  const hsnDropdownRef = useRef(null);


  useEffect(() => {
    setName(initialName || '');
    setGstRate(initialGstRate || '');
    setPrice(initialPrice || '');
    setIsTaxInclusive(initialIsTaxInclusive || false)
    setItemType(initialItemType || '');
    setUnit(initialUnit || '');
    setHsnCode(initialHsnCode || '');

    if (isEditMode && initialHsnCode) {
      const fetchInitialHsnDescription = async () => {
        setHsnLoading(true);
        setHsnError(null);
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            console.error('No authentication token found');
            setHsnError('Authentication token missing.');
            setSearchHsnTerm(initialHsnCode);
            return;
          }

          const response = await fetch(`${constant.REACT_APP_API_URL}/item/hsn?search=${initialHsnCode}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });

          if (!response.ok) {
            let errorMsg = 'Failed to fetch HSN description';
            try {
              const errResult = await response.json();
              if (errResult && errResult.message) {
                errorMsg = errResult.message;
              }
            } catch (e) { /* error */ }
            throw new Error(errorMsg);
          }

          const result = await response.json();
          if (result.success && result.data) {
            const matchingHsn = result.data.find(h => h.hsnCode === initialHsnCode);
            if (matchingHsn) {
              setSearchHsnTerm(`${matchingHsn.hsnCode}: ${matchingHsn.description}`);
            } else {
              setSearchHsnTerm(initialHsnCode);
            }
          } else {
            setSearchHsnTerm(initialHsnCode);
            if (!result.success) setHsnError(result.message || 'Failed to fetch HSN description');
          }
        } catch (error) {
          setHsnError(error.message || 'An unexpected error occurred while fetching HSN description.');
          setSearchHsnTerm(initialHsnCode);
        } finally {
          setHsnLoading(false);
        }
      };

      fetchInitialHsnDescription();
    } else if (!isEditMode) {
      setSearchHsnTerm('');
      setHsnCode('');
    }
  }, [isEditMode, initialName, initialGstRate, initialPrice, initialItemType, initialHsnCode, initialUnit]);


  useEffect(() => {
    const fetchHSNCodes = async () => {
      if (!searchHsnTerm) {
        setHsnOptions([]);
        setIsDropdownVisible(false);
        return;
      }

      setHsnLoading(true);
      setHsnError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found');
          setHsnError('Authentication token missing.');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/item/hsn?search=${searchHsnTerm}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch HSN codes');
        }

        const result = await response.json();
        if (result.success) {
          setHsnOptions(result.data);
          if (result.data.length > 0) {
            setIsDropdownVisible(true);
          } else {
            // setIsDropdownVisible(false);
          }
        } else {
          setHsnError(result.message || 'Failed to fetch HSN codes');
          setHsnOptions([]);
        }
      } catch (error) {
        setHsnError(error.message || 'An unexpected error occurred while fetching HSN codes.');
        setHsnOptions([]);
      } finally {
        setHsnLoading(false);
      }
    };

    clearTimeout(debounceTimeout.current);
    if (searchHsnTerm) {
      debounceTimeout.current = setTimeout(() => {
        fetchHSNCodes();
      }, 300);
    } else {
      setHsnOptions([]);
      setIsDropdownVisible(false);
    }


    return () => clearTimeout(debounceTimeout.current);
  }, [searchHsnTerm]);

  const handleHsnCodeSelection = (selectedHsn) => {
    setHsnCode(selectedHsn.hsnCode);
    setSearchHsnTerm(`${selectedHsn.hsnCode}: ${selectedHsn.description}`);
    setIsDropdownVisible(false);
    setHsnOptions([]);
    if (hsnInputRef.current) {
      hsnInputRef.current.blur();
    }
  };

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchHsnTerm(newSearchTerm);

    if (!newSearchTerm) {
      setHsnCode('');
      setHsnOptions([]);
      setIsDropdownVisible(false);
    } else {
      // setIsDropdownVisible(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hsnInputRef.current && !hsnInputRef.current.contains(event.target) &&
        hsnDropdownRef.current && !hsnDropdownRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  return (
    <div>
      {initialError && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{initialError}</div>}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full capitalize px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter item name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">GST Rate (%)</label>
            <select
              value={gstRate}
              onChange={(e) => setGstRate(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select GST Rate</option>
              <option value="0">0%</option>
              <option value="5">5%</option>
              <option value="12">12%</option>
              <option value="18">18%</option>
              <option value="28">28%</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter item price"
            />
          </div>

          <div className="flex items-end pb-2 pt-6 md:pt-0 md:self-end">
            <div className="flex items-center h-10">
              <input
                type="checkbox"
                id="isTaxInclusive"
                name="isTaxInclusive"
                checked={isTaxInclusive}
                onChange={(e) => setIsTaxInclusive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isTaxInclusive" className="ml-2 block text-sm font-medium text-gray-700">
                Tax Inclusive
              </label>
            </div>
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Item Type</label>
            <select
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Item Type</option>
              <option value="product">Product</option>
              <option value="service">Service</option>
            </select>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">HSN/SCN code</label>
            <input
              ref={hsnInputRef}
              type="text"
              placeholder="Search HSN/SCN code"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchHsnTerm}
              onChange={handleInputChange}
              onFocus={() => {
                if (searchHsnTerm && hsnOptions.length > 0) {
                  setIsDropdownVisible(true);
                } else if (searchHsnTerm) {
                  setIsDropdownVisible(true);
                }
              }}
            />
            {isDropdownVisible && (
              <div ref={hsnDropdownRef} className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {hsnLoading ? (
                  <div className="px-4 py-2 text-gray-500">Loading HSN Codes...</div>
                ) : hsnError ? (
                  <div className="px-4 py-2 text-red-500">{hsnError}</div>
                ) : hsnOptions.length > 0 ? (
                  <ul className="py-1">
                    {hsnOptions.map((hsn) => (
                      <li
                        key={hsn.hsnCode}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleHsnCodeSelection(hsn)}
                      >
                        {`${hsn.hsnCode}: ${hsn.description}`}
                      </li>
                    ))}
                  </ul>
                ) : searchHsnTerm && !hsnLoading ? (
                  <div className="px-4 py-2 text-gray-500">No HSN codes found for "{searchHsnTerm}"</div>
                ) : null}
              </div>
            )}
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Unit</option>
              <option value=" ">None</option>
              <option value="kgs">Kilogram (kgs)</option>
              <option value="pkt">Packet (pkt)</option>
              <option value="nos">Nos (nos)</option>
                  <option value="sqft">Square Feet (sqft)</option>
              <option value="roll">Per Roll (roll)</option>

              <option value="pcs">Pieces (pcs)</option>
              <option value="set">Set (set)</option>
              <option value="ltr">Litre (ltr)</option>
              <option value="lot">Lot (lot)</option>
              <option value="ml">Millilitre (ml)</option>
              <option value="mtr">Meter (mtr)</option>
              <option value="strp">Strip (strp)</option>
              <option value="unt">Unit (unt)</option>
              <option value="in">Inches (in)</option>
              <option value="ctn">Carton (ctn)</option>
            </select>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-4 rounded-b-lg">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={parentIsEditing || hsnLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              const itemData = { name, gstRate, price, isTaxInclusive, itemType, hsnCode, unit };
              onSubmit(itemData);
            }}
            disabled={parentLoading || parentIsEditing || hsnLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {parentIsEditing ? (isEditMode ? 'Saving Changes...' : 'Adding Item...') : (isEditMode ? 'Save Changes' : 'Add Item')}
          </button>
        </div>
      </div>
    </div>
  );
};
export default ItemForm;